import type {
  IValueTypeDetail,
  IVehicleJdPowerSave,
} from '@amsanalytics/react-shared-library';

type NormalizableValuation = {
  valueType?: string;
  mileageAdjustment: number;
  tradeInDetail?: IValueTypeDetail;
  loanDetail?: IValueTypeDetail;
  retailDetail?: IValueTypeDetail;
};

export const getCanonicalJDPowerValueType = (
  valueType?: string
): 'Trade' | 'Loan' | 'Retail' => {
  switch (valueType?.trim().toLowerCase()) {
    case 'loan':
      return 'Loan';
    case 'trade':
    case 'trade in':
    case 'trade-in':
    case 'tradein':
      return 'Trade';
    default:
      return 'Retail';
  }
};

export const getSelectedJDPowerDetail = <T extends NormalizableValuation>(
  value: T
): IValueTypeDetail | undefined => {
  switch (getCanonicalJDPowerValueType(value.valueType)) {
    case 'Loan':
      return value.loanDetail;
    case 'Trade':
      return value.tradeInDetail;
    default:
      return value.retailDetail;
  }
};

const applyMileage = (
  detail: IValueTypeDetail | undefined,
  mileage: number
): IValueTypeDetail | undefined => {
  if (!detail) return detail;
  return {
    ...detail,
    mileageAdjustment: mileage,
    bookout: detail.baseValue + detail.accessoryAdjustment + mileage,
  };
};

// JD Power proxy responses can return per-type mileage fields (MileAdjTradeIn/Loan/Retail)
// as 0 (decimal default) while the generic MileageAdj carries the real number. The shared
// library uses nullish-coalescing, which doesn't fall back on 0, so each detail's
// mileageAdjustment is persisted as 0 and the three-column print/PDF disagrees with the
// scalar-driven "Previous Pulls" row. Patch the affected detail(s) and re-derive bookout
// from parts. Mileage-specific on purpose — 0 is legitimate for accessoryAdjustment.
export const normalizeJDPowerValuation = <T extends NormalizableValuation>(
  value: T
): T => {
  if (value.mileageAdjustment === 0) return value;

  const tradeMileage = value.tradeInDetail?.mileageAdjustment ?? 0;
  const loanMileage = value.loanDetail?.mileageAdjustment ?? 0;
  const retailMileage = value.retailDetail?.mileageAdjustment ?? 0;

  if (tradeMileage === 0 && loanMileage === 0 && retailMileage === 0) {
    return {
      ...value,
      tradeInDetail: applyMileage(value.tradeInDetail, value.mileageAdjustment),
      loanDetail: applyMileage(value.loanDetail, value.mileageAdjustment),
      retailDetail: applyMileage(value.retailDetail, value.mileageAdjustment),
    };
  }

  const selected = getSelectedJDPowerDetail(value);
  if (!selected || selected.mileageAdjustment !== 0) return value;

  const patched = applyMileage(selected, value.mileageAdjustment);
  switch (getCanonicalJDPowerValueType(value.valueType)) {
    case 'Loan':
      return { ...value, loanDetail: patched };
    case 'Trade':
      return { ...value, tradeInDetail: patched };
    default:
      return { ...value, retailDetail: patched };
  }
};

export const withCanonicalJDPowerScalars = (
  value: IVehicleJdPowerSave
): IVehicleJdPowerSave => {
  const normalized = normalizeJDPowerValuation(value);
  const detail = getSelectedJDPowerDetail(normalized);
  if (!detail) return normalized;

  return {
    ...normalized,
    mileageAdjustment: detail.mileageAdjustment,
    accessoryAdjustment: detail.accessoryAdjustment,
    bookout: detail.bookout,
  };
};
